import { useState, type ReactElement } from 'react';
import type { WeeklySlot } from '@gr/core/distribution/channels/dto';
import { WEEKDAYS } from '@gr/core/distribution/channels/slots';
import { useT } from '../../../../app/di';
import { formatWeekday } from '../../../../shared/format';
import { Button } from '../../../../ui-kit/Button/Button';
import { BareInput } from '../../../../ui-kit/Field/Field';
import { ToggleMatrix } from '../../../../ui-kit/ToggleMatrix/ToggleMatrix';
import { addTime, canAddTime, isSlotOn, removeTime, slotGridOf, slotsOf, toggleSlot } from '../model/slot-grid';
import styles from './SlotGridEditor.module.scss';

export interface SlotGridEditorProps {
  slots: readonly WeeklySlot[];
  disabled: boolean;
  saving: boolean;
  onSave(slots: WeeklySlot[]): void;
}

/** CHN-UC-3: сетка «время × дни недели»; отмеченная клетка — слот. */
export function SlotGridEditor({ slots, disabled, saving, onSave }: SlotGridEditorProps): ReactElement {
  const { t, locale } = useT();
  const [grid, setGrid] = useState(() => slotGridOf(slots));
  const [newTime, setNewTime] = useState('19:00');

  return (
    <div className="u-stack">
      <ToggleMatrix
        label={t('channels.slots.title')}
        corner={t('channels.slots.time')}
        rows={grid.times}
        columns={WEEKDAYS.map((day) => ({ key: day, label: formatWeekday(day, locale) }))}
        isOn={(time, day) => isSlotOn(grid, day, time)}
        cellLabel={(time, day) => t('channels.slots.day', { day: formatWeekday(day, locale, 'long'), time })}
        onToggle={(time, day) => setGrid(toggleSlot(grid, day, time))}
        disabled={disabled}
        rowAction={(time) => (
          <Button size="sm" variant="ghost" disabled={disabled} onClick={() => setGrid(removeTime(grid, time))}>
            {t('channels.slots.remove', { time })}
          </Button>
        )}
      />
      {grid.times.length === 0 ? <p className="u-muted u-small">{t('channels.slots.empty')}</p> : null}
      {!disabled ? (
        <div className="u-row">
          <div className={styles.time}>
            <BareInput label={t('channels.slots.time')} type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} />
          </div>
          <Button icon="plus" disabled={!canAddTime(grid, newTime)} onClick={() => setGrid(addTime(grid, newTime))}>
            {t('channels.slots.add')}
          </Button>
          <Button variant="primary" loading={saving} onClick={() => onSave(slotsOf(grid))}>
            {t('channels.slots.save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
